import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import CLUB from '../../config/club.js'
import MemberPicker, { useMembersMap, memberName } from './MemberPicker.jsx'
import { Title, Card, Pill, Modal, Field, Err, NotifyBox, StagePipeline, stageTint, money, fmtDate } from './gui.jsx'
import { REPAIR_STAGES as STAGES, watchRepairs, createRepair, updateRepair, setRepairStatus, deleteRepair } from '../../lib/repairs.js'

export default function GRepairs() {
  const [raw, setRaw] = useState({})
  const [filter, setFilter] = useState('All')
  const [notifyFor, setNotifyFor] = useState(null)
  const [editFor, setEditFor] = useState(null)
  const members = useMembersMap()
  useEffect(() => { const off = watchRepairs(setRaw); return () => off() }, [])

  const rows = useMemo(() => Object.values(raw || {}).filter(Boolean).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)), [raw])
  const counts = useMemo(() => { const c = { All: rows.length }; STAGES.forEach((s) => c[s] = 0); rows.forEach((r) => { if (c[r.status] != null) c[r.status]++ }); return c }, [rows])
  const visible = filter === 'All' ? rows : rows.filter((r) => r.status === filter)

  return (
    <GShell>
      <section>
        <Title title="Club Repairs" subtitle="Intake to pickup — regrips, reshafts, loft/lie and more." />
        <div className="mt-3"><RepairForm /></div>
      </section>

      <div className="flex flex-wrap gap-2">
        <Chip label={`All ${counts.All}`} on={filter === 'All'} onClick={() => setFilter('All')} />
        {STAGES.map((s) => <Chip key={s} label={`${s} ${counts[s]}`} stage={s} on={filter === s} onClick={() => setFilter(s)} />)}
      </div>

      <section className="pb-4">
        {visible.length === 0 ? (
          <p className="text-sm text-gray-500">{rows.length === 0 ? 'No repairs yet — add one above.' : `No repairs at "${filter}".`}</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {visible.map((r) => <RepairCard key={r.id} r={r} members={members} onNotify={() => setNotifyFor(r)} onEdit={() => setEditFor(r)} />)}
          </div>
        )}
      </section>

      {notifyFor ? <NotifyModal r={notifyFor} members={members} onClose={() => setNotifyFor(null)} /> : null}
      {editFor ? <EditModal r={editFor} onClose={() => setEditFor(null)} /> : null}
    </GShell>
  )
}

function Chip({ label, stage, on, onClick }) {
  const t = stage ? stageTint(STAGES, stage) : null
  return (
    <button type="button" onClick={onClick} className="text-xs font-bold px-3 py-1.5 rounded-full transition"
      style={on ? { background: 'var(--ge-primary)', color: '#fff' } : { background: t ? t.bg : 'rgba(0,0,0,0.05)', color: t ? t.fg : '#4B5563' }}>{label}</button>
  )
}

function RepairCard({ r, members, onNotify, onEdit }) {
  const [err, setErr] = useState('')
  const idx = STAGES.indexOf(r.status)
  const t = stageTint(STAGES, r.status)
  const total = (Number(r.parts) || 0) + (Number(r.labor) || 0)
  const move = async (dir) => { const nx = STAGES[idx + dir]; if (!nx) return; try { await setRepairStatus(r.id, nx, r.memberId, r.club) } catch (e) { setErr(e?.message || 'Failed') } }
  const del = async () => { if (!window.confirm(`Delete repair for ${r.club}?`)) return; try { await deleteRepair(r.id) } catch (e) { setErr(e?.message || 'Failed') } }
  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-extrabold" style={{ color: 'var(--ge-primary)' }}>{r.club}{r.manufacturer ? <span className="text-gray-400 font-normal"> · {r.manufacturer}</span> : null}</div>
          <div className="text-sm text-gray-700">{memberName(members, r.memberId)}{r.phone ? ` · ${r.phone}` : ''}</div>
        </div>
        <Pill bg={t.bg} fg={t.fg}>{r.status}</Pill>
      </div>
      {r.repairRequested ? <div className="text-sm text-gray-800 mt-1">{r.repairRequested}</div> : null}
      <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
        {r.shaft ? <span>Shaft: {r.shaft}</span> : null}{r.grip ? <span>Grip: {r.grip}</span> : null}
        {r.loftAdj ? <span>Loft: {r.loftAdj}</span> : null}{r.lieAdj ? <span>Lie: {r.lieAdj}</span> : null}
        {r.dueDate ? <span>🗓 Due {fmtDate(r.dueDate)}</span> : null}
        <span>💵 {money(total)} ({money(r.parts)} parts + {money(r.labor)} labor)</span>
      </div>
      {r.notes ? <p className="text-xs text-gray-600 mt-1">{r.notes}</p> : null}
      <div className="mt-3"><StagePipeline stages={STAGES} status={r.status} onMove={move} /></div>
      <div className="mt-3 flex items-center gap-2 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        <button className="btn-primary text-xs" onClick={onNotify}>🔔 Notify Ready</button>
        <button className="btn-ghost text-xs" style={{ color: 'var(--ge-primary)' }} onClick={onEdit}>Edit</button>
        <button className="btn-ghost text-xs ml-auto" style={{ color: '#B91C1C' }} onClick={del}>Delete</button>
      </div>
      <Err msg={err} />
    </Card>
  )
}

function NotifyModal({ r, members, onClose }) {
  const [err, setErr] = useState('')
  const name = memberName(members, r.memberId)
  const first = (name || '').split(/\s+/)[0] || 'there'
  const msg = `Hi ${first}, your ${r.club} repair at the ${CLUB.name} pro shop is ready for pickup.`
  const mark = async () => { try { await setRepairStatus(r.id, 'Ready for Pickup', r.memberId, r.club) } catch (e) { setErr(e?.message || 'Failed') } }
  return (
    <Modal title={`Notify ${name}`} onClose={onClose}>
      {r.status !== 'Ready for Pickup' ? <button className="btn-secondary text-sm mb-3" onClick={mark}>Mark "Ready for Pickup"</button> : <p className="text-xs font-bold mb-2" style={{ color: '#15803D' }}>✓ Ready for Pickup</p>}
      <NotifyBox message={msg} />
      <Err msg={err} />
    </Modal>
  )
}

const FIELDS = [
  ['club', 'Club / item'], ['manufacturer', 'Manufacturer'], ['repairRequested', 'Repair requested'],
  ['shaft', 'Shaft'], ['grip', 'Grip'], ['loftAdj', 'Loft adj'], ['lieAdj', 'Lie adj'],
  ['parts', 'Parts $', 'number'], ['labor', 'Labor $', 'number'], ['dueDate', 'Due date', 'date'], ['phone', 'Phone'],
]

function RepairForm() {
  const empty = { memberId: '', club: '', manufacturer: '', repairRequested: '', shaft: '', grip: '', loftAdj: '', lieAdj: '', parts: '', labor: '', dueDate: '', phone: '', notes: '', photos: '' }
  const [f, setF] = useState(empty)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const submit = async () => { setBusy(true); setErr(''); try { await createRepair(f); setF(empty) } catch (e) { setErr(e?.message || 'Failed') } finally { setBusy(false) } }
  return (
    <Card>
      <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: 'var(--ge-primary)' }}>New Repair</div>
      <div className="mb-2"><MemberPicker value={f.memberId} onChange={(v) => setF((s) => ({ ...s, memberId: v }))} /></div>
      <div className="grid sm:grid-cols-3 gap-2">
        {FIELDS.map(([k, ph, type]) => <input key={k} className="input" type={type || 'text'} placeholder={ph} value={f[k]} onChange={set(k)} />)}
      </div>
      <input className="input mt-2 w-full" placeholder="Photo URLs (comma-separated, optional)" value={f.photos} onChange={set('photos')} />
      <input className="input mt-2 w-full" placeholder="Notes" value={f.notes} onChange={set('notes')} />
      <button className="btn-primary text-sm mt-2" onClick={submit} disabled={busy || !f.club.trim()}>{busy ? 'Adding…' : 'Add Repair'}</button>
      <Err msg={err} />
    </Card>
  )
}

function EditModal({ r, onClose }) {
  const [f, setF] = useState({ ...r, parts: String(r.parts ?? ''), labor: String(r.labor ?? '') })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const save = async () => { setBusy(true); setErr(''); try { await updateRepair(r.id, f); onClose() } catch (e) { setErr(e?.message || 'Failed'); setBusy(false) } }
  return (
    <Modal title={`Edit — ${r.club}`} onClose={onClose}>
      <div className="mb-2"><MemberPicker value={f.memberId} onChange={(v) => setF((s) => ({ ...s, memberId: v }))} /></div>
      <div className="grid sm:grid-cols-2 gap-2">
        {FIELDS.map(([k, ph, type]) => <Field key={k} label={ph}><input className="input w-full" type={type || 'text'} value={f[k] || ''} onChange={set(k)} /></Field>)}
      </div>
      <Field label="Notes"><input className="input w-full" value={f.notes || ''} onChange={set('notes')} /></Field>
      <div className="flex justify-end gap-2 mt-3">
        <button className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
        <button className="btn-primary text-sm" onClick={save} disabled={busy || !String(f.club || '').trim()}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
      <Err msg={err} />
    </Modal>
  )
}
